'use client';

import { AppWindowIcon, FileIcon, GamepadIcon, PictureInPicture2Icon, SettingsIcon, SmartphoneIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { SceneThumbnailPreview } from './PlayModeTooltipContent';

import type { PlayLaunchMode } from '@/editor/core/play-preview/playPreviewTypes';

import { DropdownMenuContent, DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut } from '@/components/ui/dropdown-menu';
import { Kbd } from '@/components/ui/kbd';
import { PlayLaunchMode as PlayLaunchModeValues } from '@/editor/core/play-preview/playPreviewTypes';
import { store } from '@/editor/store';
import { PlayModeSceneType } from '@/editor/store/editorStore';


interface PlayModeLaunchMenuProps {
  onOpenPreviewSettings: () => void;
  onStartPlay: (mode?: PlayLaunchMode) => void;
}

export const PlayModeLaunchMenu = observer(({
  onOpenPreviewSettings,
  onStartPlay,
}: PlayModeLaunchMenuProps) => {
  const [hoveredSceneType, setHoveredSceneType] = useState<PlayModeSceneType | null>(null);

  return (
    <DropdownMenuContent align="start" className="w-64">
      <DropdownMenuRadioGroup
        value={store.playLaunchMode}
        onValueChange={value => store.setPlayLaunchMode(value as PlayLaunchMode)}
      >
        <DropdownMenuRadioItem value={PlayLaunchModeValues.Embedded} showAsCheckbox>
          <AppWindowIcon className="size-3.5 mr-0" />
          Play in Editor
        </DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={PlayLaunchModeValues.NewWindow} showAsCheckbox>
          <PictureInPicture2Icon className="size-3.5 mr-0" />
          Play in New Window
        </DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={PlayLaunchModeValues.MobilePreview} showAsCheckbox>
          <SmartphoneIcon className="size-3.5 mr-0" />
          Play in Mobile Preview
        </DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>

      <DropdownMenuSeparator />

      <div className="w-full" onMouseLeave={() => setHoveredSceneType(null)}>
        <DropdownMenuRadioGroup
          value={store.playModeSceneType}
          onValueChange={value => store.setPlayModeSceneType(value as PlayModeSceneType)}
        >
          <DropdownMenuRadioItem
            value={PlayModeSceneType.GameDefault}
            showAsCheckbox
            onMouseEnter={() => setHoveredSceneType(PlayModeSceneType.GameDefault)}
          >
            <GamepadIcon className="size-3.5 mr-0" />
            Game Default Scene
            <DropdownMenuShortcut><Kbd>F5</Kbd></DropdownMenuShortcut>
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem
            value={PlayModeSceneType.CurrentScene}
            showAsCheckbox
            onMouseEnter={() => setHoveredSceneType(PlayModeSceneType.CurrentScene)}
          >
            <FileIcon className="size-3.5 mr-0" />
            Current Scene
            <DropdownMenuShortcut><Kbd>F6</Kbd></DropdownMenuShortcut>
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <SceneThumbnailPreview type={hoveredSceneType ?? store.playModeSceneType} />
      </div>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={onOpenPreviewSettings}>
        <SettingsIcon className="size-3.5 mr-0" />
        Play Mode Settings
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
});

PlayModeLaunchMenu.displayName = 'PlayModeLaunchMenu';
